import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AccessBlocked, CtyHeader, ReleaseNotesFooterLink, Sidenav, useScreenWidth, useTheme } from "@circuitry-ai/doc-ui";
import { SettingsNavAside } from '@circuitry-ai/doc-settings';
import { useDisclosure } from '@nextui-org/react';
import SidebarItems from '../../assets/Sidebaritems';
import expandLogo from '../../assets/web_expand_logo.svg';
import collapseLogo from '../../assets/web_collpase_logo.svg';
import { PressEvent } from '@react-types/shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Icon } from '@iconify/react';
import { BUILD_NUMBER } from '../../buildInfo';
import { clearAiInsightConversations } from '../aiInsight/conversationStore';
import { useSelector } from 'react-redux';
import { clearTenantDetailsResponse, decodeJWT, RootState, useAppDispatch, useGetFgaUserResourcesQuery } from '@circuitry-ai/doc-data';
type Props = {
  children: React.ReactNode;
};
type CustomTitles = {
  [key: string]: string;
};
const customTitles: CustomTitles = {
  dashboard: "Dashboard",
  advisors: "Advisors",
  conversations: "Conversations",
  knowledge: "Knowledge",
  data: "Data",
  analytics: "AInalysts",
  pdfscanner: "Pdf Scanner",
  ocrscanner: "OCR Scanner",
  automation: "Automations",
  'schema-mapper': "Schema Mapper",
  settings: "Settings",
  analyst: "AInalysts",

};
// Mapping between pathname segments and API resource names
const PATHNAME_TO_RESOURCE: Record<string, string> = {
  '': 'dashboard', // root path '/'
  'dashboard': 'dashboard',
  'advisors': 'advisors',
  'analytics': 'analysts', // pathname is /analytics but resource is 'analysts'
  'agents': 'agents',
  'schema-mapper': 'agents',
  'aiworkers': 'journeys', // pathname contains 'aiworkers' but resource is 'specialists'
  'knowledge': 'knowledge',
  'actions': 'actions',
  'data': 'data',
  'conversations': 'conversations',
  'history': 'agent_history',
  'triage': 'triage_feedback',
  'uxgenie': 'uxgenie',
  'formgenie': 'uxgenie', // reuse uxgenie resource until formgenie has its own FGA entry
  'genie': 'uxgenie',
  'scheduler': 'scheduler',
  'settings': 'settings',
};

/** Application resource id required for FGA route gate (Layout), or undefined if no gate. */
function getRequiredApplicationResourceForPath(pathname: string): string | undefined {
  if (pathname === '/' || pathname === '') {
    return PATHNAME_TO_RESOURCE[''];
  }
  if (pathname.includes('/aiworkers/list/journeys')) {
    return PATHNAME_TO_RESOURCE['aiworkers'];
  }
  const firstSegment = pathname.split('/').filter(Boolean)[0] ?? '';
  return PATHNAME_TO_RESOURCE[firstSegment];
}

const Layout = ({ children }: Props) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { isDark } = useTheme();
  const logoSrc = isDark ? '/Logo-dark.svg' : '/Logo.svg';
  const { logout, loginWithRedirect } = useAuth0();
  const dispatch = useAppDispatch();
  const pathname = useLocation().pathname;
  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const userId = user?.sub;
  const authToken = useSelector((state: RootState) => state.auth.token);
  const decodedAuthToken = useMemo(() => (authToken ? decodeJWT(authToken) : null), [authToken]);
  const idpTenantId = String(decodedAuthToken?.tenant_id ?? decodedAuthToken?.['https://circuitry.ai/tenant_id'] ?? '');
  const idpCustomer = String(decodedAuthToken?.org_name ?? '');
  const width = useScreenWidth();
  const isMobile = width < 640;
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => isMobile || JSON.parse(localStorage.getItem("sidenavCollapsed") ?? "true"));
  const [blockedResource, setBlockedResource] = useState<string | null>(null);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const suppressSettingsMenu = useRef(false);
  const closeSettingsMenu = () => {
    suppressSettingsMenu.current = true;
    setSettingsMenuOpen(false);
    window.setTimeout(() => {
      suppressSettingsMenu.current = false;
    }, 400);
  };
  const { refetch: refetchUserResources } = useGetFgaUserResourcesQuery(userId || '', {
    skip: !userId,
  });

  const handleToggleSidenav = () => {
    if (sidenavRef.current) {
      sidenavRef.current?.toggle();
    }
  };

  const sidebarItems = SidebarItems();
  const pathnameRef = useRef(pathname);
  const sidenavRef = useRef<{ toggle: () => void } | null>(null);
  const navigate = useNavigate();
  const { search } = useLocation();
  const currentLocation = pathnameRef.current.split('/')[1] ?? 'dashboard';
  const isChatView = pathname.includes('/advisors/') && search.includes('tab=converse');
  // The Analyst Agent's own pages (a saved dashboard, the agent editor) keep the chrome down
  // (Portkey style): no header and no footer, the left nav stays. Its tab on /analytics keeps both.
  const isAiInsight = /^\/analytics\/analyst-agent\//.test(pathname);

  // Check access on every pathname change (navigation)
  useEffect(() => {
    pathnameRef.current = pathname;
    setBlockedResource(null);

    if (!userId) {
      return;
    }

    const resourceName = getRequiredApplicationResourceForPath(pathname);
    if (!resourceName) {
      return;
    }

    let cancelled = false;

    const checkAccess = async () => {
      const result = await refetchUserResources();
      if (cancelled) return;

      const resourcesData = result.data?.data?.resources;

      if (!resourcesData?.application_resources) {
        return;
      }

      const resources = resourcesData.application_resources;
      const matchedResource = resources.find(
        (resource: any) => {
          const idMatch = resource.id?.toLowerCase() === resourceName.toLowerCase();
          const nameMatch = resource.name?.toLowerCase() === resourceName.toLowerCase();
          const relationMatch = resource.relation === 'view';
          return (idMatch || nameMatch) && relationMatch;
        }
      );

      if (!matchedResource) {
        setBlockedResource(resourceName);
      }
    };

    void checkAccess();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, userId]);

  useEffect(() => {
    document.title = customTitles[currentLocation] || 'Circuitry AI';
  }, [currentLocation]);

  const onSideNavClick = (e: PressEvent) => {
    // Use 'as HTMLElement' to access dataset (fix TS error)
    const clickedKey = (e.target as HTMLElement).dataset.key ?? 'dashboard';
    document.title = customTitles[clickedKey] || 'Circuitry AI';

    if (clickedKey === "help") {
      let helpUrl = "https://customer.circuitry.ai/knowledge-base";
      const currentPath = pathnameRef.current;
      if (currentPath.includes("/createadvisor")) {
        helpUrl += "/getting-started-with-product-Advisor";
      } else if (currentPath.includes("/advisors")) {
        helpUrl += "/viewing-and-editing-advisors";
      } else if (currentPath.includes("/conversations")) {
        helpUrl += "/viewing-conversation-history";
      } else if (currentPath.includes("/knowledge")) {
        helpUrl += "/using-the-knowledge-base";
      } else if (currentPath.includes("/settings")) {
        helpUrl += "/settings";
      } else {
        helpUrl += "/dashboard-view";
      }
      window.open(helpUrl, "_blank");
      return;
    }

    if (clickedKey === 'toggle') {
      handleToggleSidenav();
      return;
    }

    // Access check happens in useEffect on pathname change
    // Navigation will trigger pathname change which will check access
    if (isOpen) {
      onOpenChange();  // This closes the modal
    }
  };
  if (pathname.includes('/file/')) {
    return <div>{children}</div>;
  }

  const handleCollapse = (isCollapsed: boolean) => {
    setIsCollapsed(isCollapsed);
    (!isMobile) && localStorage.setItem("sidenavCollapsed", JSON.stringify(isCollapsed));
  };

  const handleOrganisationSwitch = (name: string) => {
    dispatch(clearTenantDetailsResponse());
    sessionStorage.removeItem('advisor_id');  // Remove advisor_id from session storage
    loginWithRedirect({
      authorizationParams: {
        organization: name
      }
    });
  };

  const userLogout = () => {
    clearAiInsightConversations();
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  const sidenavProps = {
    isOpen,
    onOpenChange,
    onClick: onSideNavClick,
    items: sidebarItems,
    onCollapseChange: handleCollapse,
    switchOrganisation: handleOrganisationSwitch,
    doLogout: userLogout,
    isCompact: isCollapsed,
    showUserFooter: false,
  };

  const mainContent =
    blockedResource !== null ? (
      <AccessBlocked resourceName={blockedResource ?? undefined} />
    ) : (
      children
    );

  return (
    <div className="flex flex-col h-screen ">

      <div className="flex fixed left-0 h-full w-full z-10">
        <div
          className={`relative flex h-full shrink-0 flex-col justify-between overflow-visible border-r border-default-200 bg-content1 dark:border-[#30363d] dark:bg-[#010409] !duration-100 transition-width ${isCollapsed ? 'sm:w-[72px]' : 'sm:w-[220px]'}`}
          onMouseOver={(event) => {
            if (suppressSettingsMenu.current) return;
            const item = (event.target as HTMLElement).closest('[id^="sidebar-item-"]');
            if (!item) return;
            setSettingsMenuOpen(item.id === 'sidebar-item-Settings');
          }}
          onMouseLeave={() => setSettingsMenuOpen(false)}
        >
          <div className="relative hidden h-[54px] w-full shrink-0 sm:flex sm:items-center">
            {isCollapsed ? (
              <div className="absolute right-0 top-1/2 z-[41] flex -translate-y-1/2 translate-x-4 items-center gap-1.5 rounded-large py-1 pl-1.5 pr-0">
                <button
                  type="button"
                  aria-label="Home"
                  onClick={() => navigate('/')}
                  className="m-0 flex cursor-pointer items-center border-0 bg-transparent p-0"
                >
                  <img src={collapseLogo} alt="Circuitry" className="h-8 w-8 object-contain" />
                </button>
                <button
                  type="button"
                  aria-label="Expand sidebar"
                  onClick={handleToggleSidenav}
                  className="m-0 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[8px] border border-[#E4E4E7] bg-content1 p-0 text-[#71717a] hover:bg-[#F4F4F5] hover:text-[#18181B] dark:border-divider dark:hover:bg-content2 dark:hover:text-foreground"
                >
                  <Icon icon="lucide:panel-left-open" width={18} height={18} className="text-current" />
                </button>
              </div>
            ) : (
              <div className="flex w-full items-center justify-between px-3">
                <button
                  type="button"
                  aria-label="Home"
                  onClick={() => navigate('/')}
                  className="m-0 flex min-w-0 cursor-pointer items-center border-0 bg-transparent p-0"
                >
                  <img src={expandLogo} alt="Logo" className="h-[30px] w-auto object-contain object-left" />
                </button>
                <button
                  type="button"
                  aria-label="Collapse sidebar"
                  onClick={handleToggleSidenav}
                  className="m-0 flex h-[34px] w-[34px] shrink-0 cursor-pointer items-center justify-center rounded-[8px] border border-[#E4E4E7] bg-content1 p-0 text-[#71717a] hover:bg-[#F4F4F5] hover:text-[#18181B] dark:border-divider dark:hover:bg-content2 dark:hover:text-foreground"
                >
                  <Icon icon="lucide:panel-left-close" width={18} height={18} className="text-current" />
                </button>
              </div>
            )}
          </div>
          <div className='flex-1 overflow-y-auto'>
            <Sidenav ref={sidenavRef} {...sidenavProps} />
          </div>
          {settingsMenuOpen && (
            <SettingsNavAside
              className="absolute left-full top-[54px] z-50 h-full max-h-[calc(100%-54px)] overflow-hidden rounded-[16px] shadow-medium"
              onNavigate={closeSettingsMenu}
            />
          )}
        </div>
        <div className="relative flex h-full min-w-0 flex-1 flex-col">
          {isAiInsight ? null : (
            <CtyHeader
              logo={logoSrc}
              onOpen={onOpen}
              onLogout={userLogout}
              onSwitchOrganisation={handleOrganisationSwitch}
            />
          )}
          {!isChatView && !isAiInsight ? (
            <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
              <div className="cty-page-inset min-h-0 flex-1 overflow-hidden">{mainContent}</div>
              <footer className="p-2 text-center text-xs text-default-500 border dark:text-[#8a8a8a] dark:border-[#30363d] dark:bg-[#010409]">
                Copyright Circuitry.ai {new Date().getFullYear()} / <a href="/privacy-policy" className="underline">Privacy Policy</a> / <ReleaseNotesFooterLink
                  tenantId={idpTenantId}
                  customer={idpCustomer}
                  token={authToken}
                  fallback={<>V {BUILD_NUMBER}</>}
                />
              </footer>
            </div>
          ) : (
            <div className='flex-1 overflow-hidden layout-content'>{mainContent}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Layout;




